import { Injectable } from '@angular/core';
import { AppHttpService } from './app-http.service';
import { JobCode, Organization, Project } from '../models';
import { HttpResponse } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class JobCodeService {

  constructor(
    private appHttpService: AppHttpService
  ) {}

  /**
   * Returns JobCodes with pagination
   * @param org Organization
   * @param pagination start
   * @param qp query params
   * @returns {Promise<any>} {meta, result: JobCode[]}
   */
  getRecords(org: Organization, pagination, qp: any = {}) {
    qp.start = pagination.pageIndex * pagination.pageSize;
    qp.total = pagination.pageSize;

    return this.appHttpService
      .getService(`/v2/organization/${org?.id}/job-codes`, qp)
      .toPromise()
      .then(resp => {
        if (resp.data && Array.isArray(resp.data)) {
          const result = resp.data.map(r => new JobCode(r));
          const meta = Object.assign(resp.meta || {}, { type: resp.type });
          return { result, meta };
        }
        throw new Error('Error parsing response');
      });
  }

  /**
   * Returns JobCodes
   * @param project Project
   * @param qp query params
   * @returns {Promise<JobCode[]>}
   */
  getProjectRecords(project: Project, qp: any = {}): Promise<JobCode[]> {
    return this.appHttpService
      .getService(`/v2/project/${project?.id}/job-codes`, qp)
      .toPromise()
      .then(resp => {
        if (resp.data && Array.isArray(resp.data)) {
          return resp.data.map(r => new JobCode(r));
        }
        throw new Error('Error parsing response');
      });
  }

  /**
   * Creates a new JobCode
   * @param payload json payload
   * @param qp query params
   * @returns {Promise<JobCode>}
   */
  create(payload: any = null, qp: any = {}): Promise<JobCode> {
    return this.appHttpService
      .postService(`/v2/organization/${payload?.organization_id}/job-code`, payload, qp)
      .toPromise()
      .then((resp: any) => {
        if (resp.data) {
          return new JobCode(resp.data);
        }
        throw new Error('Error parsing response');
      });
  }

  /**
   * Updates JobCode
   * @param payload json payload
   * @param qp query params
   * @returns {Promise<JobCode>}
   */
  update(payload: any = null, qp: any = {}): Promise<JobCode> {
    return this.appHttpService
      .putService(`/v2/job-code/${payload?.id}`, payload, qp)
      .toPromise()
      .then((resp: any) => {
        if (resp.data) {
          return new JobCode(resp.data);
        }
        throw new Error('Error parsing response');
      });
  }

  /**
   * Deletes JobCode
   * @param model JobCode
   * @returns {Promise<any>} {status, message}
   */
  delete(model: JobCode): Promise<HttpResponse<any>> {
    return this.appHttpService
      .deleteService(`/v2/job-code/${model?.id}`)
      .toPromise();
  }

  /**
   * Add JobCode to Project
   * @param jobCode JobCode
   * @param project Project
   * @returns {Promise<string>} message
   */
  addToProject(jobCode: JobCode, project: Project): Promise<string> {
    return this.appHttpService
      .putService(`/v2/project/${project?.id}/job-code/${jobCode.id}`, {})
      .toPromise()
      .then((resp: any) => resp?.message);
  }

  /**
   * Remove JobCode from Project
   * @param jobCode JobCode
   * @param project Project
   * @returns {Promise<string>} message
   */
  removeFromProject(jobCode: JobCode, project: Project): Promise<string> {
    return this.appHttpService
      .deleteService(`/v2/project/${project?.id}/job-code/${jobCode.id}`, {})
      .toPromise()
      .then((resp: any) => resp?.message);
  }
}
